import axios, { AxiosError, AxiosResponse, InternalAxiosRequestConfig, AxiosInstance } from 'axios';
import { BehaviorSubject } from 'rxjs';
import { getAuthTokensService } from './AuthTokensService';

interface AuthService {
  refreshToken: () => Promise<any>;
  logout: () => Promise<void>;
}

const isRefreshing = new BehaviorSubject<boolean>(false);

export const setupAuthInterceptor = (
  apiBaseUrl: string,
  authService: AuthService,
  navigateToLogin: () => void,
  getQueryParam?: (param: string) => string | null,
  axiosInstance?: AxiosInstance
) => {
  const instance = axiosInstance || axios;
  console.log('Setting up auth interceptor with apiBaseUrl:', apiBaseUrl);
  
  // Request interceptor
  instance.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      const tokenStorage = getAuthTokensService();
      
      const baseURL = config.baseURL || '';
      const url = config.url || '';
      const fullUrl = baseURL + url;

      console.log('Auth interceptor - request:', {
        url,
        baseURL,
        fullUrl,
        includesApiBaseUrl: url.includes(apiBaseUrl),
        includesAnonymous: url.includes('anonymous'),
        apiBaseUrl
      });

      if (!fullUrl.includes(apiBaseUrl) || fullUrl.includes('anonymous')) {
        console.log('Auth interceptor - skipping auth header for URL:', fullUrl);
        return config;
      }

      let access_token: string | undefined = tokenStorage.getCurrentCredentials()?.access_token?.token;

      // Handle reset-password and confirm token scenarios
      if (
        (window.location.href.includes('reset-password') ||
          window.location.href.includes('confirm')) &&
        window.location.href.includes('token') &&
        getQueryParam
      ) {
        access_token = getQueryParam('token') || undefined;
      }

      console.log('Auth interceptor - adding Authorization header:', {
        hasToken: !!access_token,
        tokenPreview: access_token ? `${access_token.substring(0, 20)}...` : 'none'
      });

      if (access_token) {
        config.headers.Authorization = `Bearer ${access_token}`;
      }

      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Response interceptor
  instance.interceptors.response.use(
    (response: AxiosResponse) => {
      return response;
    },
    async (error: AxiosError) => {
      const tokenStorage = getAuthTokensService();
      const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

      console.error(error);

      // Handle refresh token endpoint 401
      if (originalRequest?.url?.includes('/refresh') && error.response?.status === 401) {
        console.log('Auth interceptor: Refresh token failed, clearing tokens and navigating to login');
        tokenStorage.clearTokens();
        navigateToLogin();
        return Promise.reject(error);
      }

      // Handle general 401 errors
      if (
        error.response?.status === 401 &&
        !originalRequest?.url?.includes('/login') &&
        !originalRequest._retry
      ) {
        console.log('🚨 Auth interceptor: 401 error detected, attempting token refresh for URL:', originalRequest?.url);
        return handle401Error(originalRequest, tokenStorage, authService, navigateToLogin, instance, apiBaseUrl);
      }

      return Promise.reject(error);
    }
  );
};

const handle401Error = async (
  originalRequest: InternalAxiosRequestConfig & { _retry?: boolean },
  tokenStorage: ReturnType<typeof getAuthTokensService>,
  _authService: AuthService,
  navigateToLogin: () => void,
  axiosInstance: AxiosInstance,
  apiBaseUrl: string
): Promise<AxiosResponse> => {
  if (!isRefreshing.getValue()) {
    isRefreshing.next(true);
    
    const refreshToken = tokenStorage.getRefreshToken();
    const accessToken = tokenStorage.getCurrentCredentials()?.access_token?.token;
    
    console.log('handle401Error: Refresh token available:', !!refreshToken);
    console.log('handle401Error: Access token available:', !!accessToken);
    
    if (refreshToken && accessToken) {
      try {
        console.log('🔄 Auth interceptor: Starting backend token refresh');
        console.log('🔍 Current credentials before refresh:', {
          accessToken: accessToken.substring(0, 20) + '...',
          exp: tokenStorage.getCurrentCredentials()?.access_token?.exp,
          refreshToken: refreshToken.substring(0, 20) + '...'
        });
        
        // Call backend refresh endpoint
        const refreshResponse = await axiosInstance.post(`${apiBaseUrl}/api/auth/refresh`, {
          access_token: accessToken,
          refresh_token: refreshToken
        });
        
        if (refreshResponse.data) {
          const tokenData = refreshResponse.data;
          console.log('✅ Backend refresh successful, received token data:', {
            fresh: tokenData.fresh,
            exp: tokenData.exp,
            tenant_id: tokenData.tenant_id,
            roles: tokenData.roles
          });
          
          // Extract token string from response
          const newAccessToken = tokenData.token;
          
          const oldToken = accessToken;
          console.log('🔄 Auth interceptor: Updating token storage with new tokens');
          console.log('🔄 Auth interceptor: Old access token preview:', oldToken?.substring(0, 20) + '...');
          console.log('🔄 Auth interceptor: New access token preview:', newAccessToken?.substring(0, 20) + '...');
          console.log('🔍 Token comparison:');
          console.log('  - Tokens are identical:', oldToken === newAccessToken);
          console.log('  - Old token length:', oldToken?.length);
          console.log('  - New token length:', newAccessToken?.length);
          
          if (oldToken === newAccessToken) {
            console.error('❌ PROBLEM: Refresh returned the same token! This will cause infinite 401 loop');
          }
          
          // Create new credentials object matching AuthTokensService format
          const newCredentials = {
            access_token: {
              token: newAccessToken,
              roles: tokenData.roles || [],
              tenant_id: tokenData.tenant_id || '',
              exp: tokenData.exp // This is already an ISO string from TokenTemplateDTO
            },
            refresh_token: {
              token: refreshToken, // Keep existing refresh token
              exp: '', // Will be populated from JWT if needed
            },
          };
          
          // Force update credentials immediately
          tokenStorage.forceUpdateCredentials(newCredentials);
          console.log('🔄 Auth interceptor: Force updated credentials, new token preview:', tokenStorage.getCurrentCredentials()?.access_token?.token?.substring(0, 20) + '...');
          
          // Update original request with new token
          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
          originalRequest._retry = true;
          
          isRefreshing.next(false);
          
          return axiosInstance(originalRequest);
        } else {
          // Backend returned null - token refresh failed, should logout
          console.log('❌ Backend refresh returned null - token refresh failed, logging out');
          throw new Error('Backend refresh returned null');
        }
      } catch (refreshError: any) {
        console.error('❌ Backend refresh failed:', refreshError);
        isRefreshing.next(false);
        
        // Check if it's a 403 (token not expired - authorization issue)
        if (refreshError.response?.status === 403) {
          console.log('❌ Backend refresh: 403 error - token not expired, this is an authorization issue');
          // Don't logout for authorization issues, just let the 401 bubble up
          return Promise.reject(refreshError);
        }
        
        // For other errors, clear tokens and logout
        console.log('❌ Backend refresh failed, clearing tokens and logging out');
        tokenStorage.clearTokens();
        navigateToLogin();
        return Promise.reject(refreshError);
      }
    } else {
      console.log('handle401Error: Missing refresh token or access token, navigating to login');
      isRefreshing.next(false);
      tokenStorage.clearTokens();
      navigateToLogin();
      return Promise.reject(new Error('No refresh token or access token available'));
    }
  }

  // Wait for the token refresh to complete
  return new Promise((resolve, reject) => {
    const subscription = isRefreshing.subscribe((refreshing) => {
      if (!refreshing) {
        subscription.unsubscribe();
        
        const currentToken = tokenStorage.getCurrentCredentials()?.access_token?.token;
        if (currentToken) {
          originalRequest.headers.Authorization = `Bearer ${currentToken}`;
          originalRequest._retry = true;
          resolve(axiosInstance(originalRequest));
        } else {
          reject(new Error('Token refresh failed'));
        }
      }
    });
  });
};

